import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import OrgChartEdges from './OrgChartEdges';
import OrgChartNode from './OrgChartNode';

/**
 * The pan/zoom viewport.
 *
 * One transform on one wrapper moves everything — cards and connectors alike —
 * so they can never drift apart. `transform-origin: 0 0` matters: with the
 * default centre origin, the translate the zoom maths computes would be applied
 * around a moving point and the anchor-under-cursor behaviour would break.
 *
 * `captureRef` points at the untransformed content layer, which is what the PNG
 * export measures and captures — exporting the viewport instead would produce a
 * picture of the current scroll position rather than of the chart.
 */
const OrgChartCanvas = forwardRef(({
  nodes,
  edges,
  width,
  height,
  transform,
  isPanning,
  panHandlers,
  focusedId,
  onToggle,
  onFocus,
  onChangeManager,
  onEdit,
  wasDragged,
  captureRef,
  dragHandlersFor,
  draggingId,
  dropTargetId,
}, viewportRef) => (
  <div
    ref={viewportRef}
    {...panHandlers}
    className={`tw-relative tw-overflow-hidden tw-bg-slate-50 tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-select-none ${isPanning ? 'tw-cursor-grabbing' : 'tw-cursor-grab'}`}
    style={{ height: 'max(320px, calc(100dvh - 260px))', minHeight: 420, touchAction: 'none' }}
  >
    {/* Dotted ground, so panning an empty area still reads as movement. */}
    <div
      className="tw-absolute tw-inset-0 tw-pointer-events-none"
      style={{
        backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)',
        backgroundSize: `${24 * transform.k}px ${24 * transform.k}px`,
        backgroundPosition: `${transform.x}px ${transform.y}px`,
        opacity: 0.5,
      }}
    />

    <div
      style={{
        transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.k})`,
        transformOrigin: '0 0',
        width,
        height,
        position: 'absolute',
        top: 0,
        left: 0,
      }}
    >
      <div ref={captureRef} style={{ width, height, position: 'relative' }} className="tw-bg-slate-50">
        <OrgChartEdges edges={edges} width={width} height={height} />

        {nodes.map((node) => (
          <div
            key={node.id}
            style={{ position: 'absolute', left: node.x, top: node.y }}
          >
            <OrgChartNode
              node={node}
              isFocused={focusedId === node.id}
              isDragging={draggingId === node.id}
              isDropTarget={dropTargetId === node.id}
              onToggle={onToggle}
              onFocus={onFocus}
              onChangeManager={onChangeManager}
              onEdit={onEdit}
              wasDragged={wasDragged}
              dragHandlers={dragHandlersFor(node)}
            />
          </div>
        ))}
      </div>
    </div>
  </div>
));

OrgChartCanvas.displayName = 'OrgChartCanvas';

OrgChartCanvas.propTypes = {
  nodes: PropTypes.array.isRequired,
  edges: PropTypes.array.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  transform: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number, k: PropTypes.number }).isRequired,
  isPanning: PropTypes.bool,
  panHandlers: PropTypes.object.isRequired,
  focusedId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onToggle: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  onChangeManager: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  wasDragged: PropTypes.func.isRequired,
  captureRef: PropTypes.object,
  dragHandlersFor: PropTypes.func.isRequired,
  draggingId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dropTargetId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default OrgChartCanvas;
